import { LegalLayout } from "./_legal-layout";

export default function PrivacyPage() {
  return (
    <LegalLayout
      kicker="// legal · 02"
      title={
        <>
          Privacy <span className="font-italic-serif text-secondary">Policy.</span>
        </>
      }
      effectiveDate="January 1, 2026"
      intro={
        <p>
          MainLoop teaches teenagers, so protecting student privacy is fundamental to
          what we do. This Privacy Policy explains what information we collect, how we
          use it, and the choices you have. Because many of our students are minors, we
          design with the Children&rsquo;s Online Privacy Protection Act (COPPA), the
          Family Educational Rights and Privacy Act (FERPA, where applicable), and
          state-level student-data laws in mind.
        </p>
      }
    >
      <h2>1. Information we collect</h2>

      <h3>Account &amp; profile</h3>
      <ul>
        <li>Name, email, and (for students under 13) parent/guardian email.</li>
        <li>Authentication identifiers from our auth provider (Clerk).</li>
        <li>Optional profile details you choose to add.</li>
      </ul>

      <h3>Enrollment &amp; learning activity</h3>
      <ul>
        <li>Courses you enroll in, lessons you complete, and homework submissions.</li>
        <li>
          Code snippets you save, including the lesson context they were saved in. If
          you choose to share a snippet via a public link (<code>/s/:id</code>), the
          snippet contents become readable by anyone with that link.
        </li>
        <li>Messages exchanged with instructors through the in-app messaging tools.</li>
      </ul>

      <h3>Payments</h3>
      <ul>
        <li>
          Tuition payments are processed by Stripe. We receive transaction metadata
          (amount, status, last four digits of the card) but do not store your full card
          number.
        </li>
      </ul>

      <h3>Technical data</h3>
      <ul>
        <li>IP address, browser, device type, and pages visited.</li>
        <li>Cookies and similar technologies (see our Cookie Policy).</li>
      </ul>

      <h2>2. How we use information</h2>
      <ul>
        <li>To deliver classes, lessons, and the in-app coding sandbox.</li>
        <li>To process enrollments and payments.</li>
        <li>
          To communicate about your account, your classes, and changes to the Service.
        </li>
        <li>
          To improve our courses, catch bugs, and keep the Service safe and secure.
        </li>
        <li>To comply with our legal obligations.</li>
      </ul>
      <p>
        We do <strong>not</strong> sell student personal information, and we do not use
        student data to deliver targeted advertising.
      </p>

      <h2>3. Children under 13</h2>
      <p>
        For students under 13, we obtain verifiable parental consent before collecting
        personal information beyond what is needed to request that consent. Parents may:
      </p>
      <ul>
        <li>Review the personal information we have collected from their child;</li>
        <li>Request that we delete it;</li>
        <li>Refuse to allow further collection or use of their child&rsquo;s information.</li>
      </ul>
      <p>
        To exercise these rights, contact{" "}
        <a href="mailto:info@mainloopacademy.com">info@mainloopacademy.com</a>.
      </p>

      <h2>4. School &amp; FERPA arrangements</h2>
      <p>
        When MainLoop is used through a partnership with a school or district, we act as
        a &ldquo;school official&rdquo; under FERPA where applicable, and use student
        education records only for the educational purposes the school has authorized.
        Schools may contact us to access, correct, or delete their students&rsquo; data.
      </p>

      <h2>5. How we share information</h2>
      <p>We share personal information only in the following circumstances:</p>
      <ul>
        <li>
          <strong>Service providers</strong> who help us run MainLoop, under contracts
          that limit their use of the data — including Clerk (authentication), Stripe
          (payments), Zoom (live class video), our database and hosting provider, and
          email delivery providers.
        </li>
        <li>
          <strong>AI providers</strong>, when you use AI Features. Inputs you submit may
          be sent to a third-party model provider for processing. We do not knowingly
          send personal information about other students to AI providers.
        </li>
        <li>
          <strong>With your direction</strong>, such as when you publish a snippet at a
          public share URL.
        </li>
        <li>
          <strong>For legal reasons</strong>, when required by law or to protect the
          safety of users.
        </li>
        <li>
          <strong>Business transfers</strong>, if MainLoop is involved in a merger,
          acquisition, or sale of assets — in which case we will notify affected users
          before any data is transferred.
        </li>
      </ul>

      <h2>6. Data retention</h2>
      <p>
        We keep account information for as long as your account is active. Code snippets
        and homework submissions are retained while you remain enrolled and for a
        reasonable period afterward so you can return to your work. You can ask us to
        delete your account and associated data at any time.
      </p>

      <h2>7. Security</h2>
      <p>
        We use industry-standard safeguards (encryption in transit, hashed credentials,
        access controls, audit logging) to protect personal information. No service can
        guarantee perfect security; we will notify affected users promptly if we become
        aware of a security incident affecting their data.
      </p>

      <h2>8. Your rights</h2>
      <p>
        Depending on where you live, you may have the right to access, correct, delete,
        or export your personal information, or to object to or limit certain uses. To
        exercise these rights, email{" "}
        <a href="mailto:info@mainloopacademy.com">info@mainloopacademy.com</a>.
      </p>

      <h2>9. International users</h2>
      <p>
        MainLoop is operated from the United States. If you access the Service from
        outside the U.S., you understand that your information may be transferred to and
        processed in the U.S.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        If we make material changes, we will notify you and post the updated policy here
        with a new effective date.
      </p>

      <h2>11. Contact</h2>
      <p>
        Questions or concerns? Email{" "}
        <a href="mailto:info@mainloopacademy.com">info@mainloopacademy.com</a>. MainLoop
        Education is based in New York, NY, USA.
      </p>
    </LegalLayout>
  );
}
